import * as React from 'react';
import { Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  handshakeDotRecipe,
  handshakeDotsRecipe,
  handshakeRecipe,
  partnerLogoImageRecipe,
  partnerLogoRecipe,
  type PartnerLogoRecipeProps,
} from '../recipes/partner.recipe';
import { Icon, IconTile } from './Icon';

export interface PartnerLogoProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    PartnerLogoRecipeProps {
  /** The logo. Nothing, null or empty draws the fallback glyph instead. */
  src?: string | null;
  alt?: string;
  /** Shown when there is no logo. A truck, unless told otherwise. */
  fallback?: LucideIcon;
  /** The glyph's colour when the box has a brand colour behind it. */
  fallbackTone?: 'default' | 'on-primary';
}

/**
 * A third party's mark, contained in a box.
 *
 *   <PartnerLogo src={partner.logo} alt={partner.name}
 *     style={{ backgroundColor: partner.logoBackgroundColor }} />
 *   <PartnerLogo variant="ring" src={integration.modalLogo} alt={integration.name} />
 *
 * The brand colour behind a `tile` is data — each partner supplies its own —
 * so it arrives as a `style`, which is the one place a literal colour is
 * meant to be.
 */
export const PartnerLogo = React.forwardRef<HTMLSpanElement, PartnerLogoProps>(
  function PartnerLogo(
    {
      src,
      alt = '',
      variant,
      fallback = Truck,
      fallbackTone = 'default',
      className,
      ...props
    },
    ref
  ) {
    return (
      <span
        ref={ref}
        className={cn(partnerLogoRecipe({ variant }), className)}
        {...props}
      >
        {src ? (
          <img src={src} alt={alt} className={partnerLogoImageRecipe()} />
        ) : (
          <Icon
            icon={fallback}
            size="lg"
            tone={fallbackTone === 'on-primary' ? 'inherit' : 'default'}
            className={
              fallbackTone === 'on-primary'
                ? 'text-primary-foreground'
                : undefined
            }
          />
        )}
      </span>
    );
  }
);

export interface ConnectionHandshakeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The near end — this device, usually. */
  fromIcon: LucideIcon;
  /** The far end — the company, usually. */
  toIcon: LucideIcon;
  /** The partner in the middle. */
  logo?: string | null;
  logoAlt?: string;
  /** Stands in for the partner when it has no logo. */
  fallback?: LucideIcon;
}

/**
 * The picture at the top of the connection dialog: two ends joined through
 * the partner being connected.
 *
 *   <ConnectionHandshake fromIcon={Smartphone} toIcon={Building}
 *     logo={integration.modalLogo} logoAlt={integration.name} />
 */
export const ConnectionHandshake = React.forwardRef<
  HTMLDivElement,
  ConnectionHandshakeProps
>(function ConnectionHandshake(
  { fromIcon, toIcon, logo, logoAlt, fallback, className, ...props },
  ref
) {
  const dots = (
    <span className={handshakeDotsRecipe()} aria-hidden="true">
      <span className={handshakeDotRecipe()} />
      <span className={handshakeDotRecipe()} />
      <span className={handshakeDotRecipe()} />
    </span>
  );
  return (
    <div ref={ref} className={cn(handshakeRecipe(), className)} {...props}>
      <IconTile icon={fromIcon} size={12} shape="circle" iconTone="muted" />
      {dots}
      <PartnerLogo
        variant="ring"
        src={logo}
        alt={logoAlt}
        fallback={fallback}
      />
      {dots}
      <IconTile icon={toIcon} size={12} shape="circle" iconTone="muted" />
    </div>
  );
});
